import moment from 'moment-timezone';

// Display-only time-zone handling for the Schedule Management grid.
// Stored schedule times are NOT modified. Conversions use the shift's UTC instant
// (startTimeUTC/endTimeUTC) and IANA zones so DST is handled correctly -- never
// hardcoded offsets.
//
// "My Time"    -> the logged-in viewer's zone (configured, else browser-detected).
// "Agent Time" -> the employee's configured IANA zone. If the employee has no zone,
//                 the raw stored startTime/endTime is already the employee's local
//                 scheduled time, so it is shown as-is (not a guess) and flagged unknown.

export type TimeDisplayMode = 'mine' | 'agent';

export interface ShiftTimeInput {
  date?: string | null;          // YYYY-MM-DD, the employee-local scheduled date
  startTime?: string | null;     // HH:MM, employee-local (raw, authoritative stored value)
  endTime?: string | null;
  startTimeUTC?: string | null;  // full UTC datetime (instant) for conversion
  endTimeUTC?: string | null;
  userTimezone?: string | null;  // employee IANA zone (if provided on the row)
  user?: { timezone?: string | null } | null;
}

export const isValidZone = (tz?: string | null): tz is string => !!tz && !!moment.tz.zone(tz);

/** Logged-in user's zone: configured IANA, else browser-detected, else UTC. Never assumes Texas. */
export function resolveViewerTimezone(user?: { timezone?: string | null } | null): string {
  if (isValidZone(user?.timezone)) return user!.timezone!;
  try {
    const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (isValidZone(browser)) return browser;
  } catch { /* ignore */ }
  return 'UTC';
}

/** Employee's configured IANA zone, or null if unknown (do not guess from work location). */
export function resolveAgentTimezone(shift: ShiftTimeInput): string | null {
  const tz = shift.user?.timezone ?? shift.userTimezone ?? null;
  return isValidZone(tz) ? tz : null;
}

function hhmm(raw?: string | null): string {
  const s = (raw || '').trim();
  return s.length >= 5 ? s.slice(0, 5) : s;
}

// One time's display in the target zone, with its calendar-day offset vs the
// scheduled (employee-local) date -- so an overnight shift that lands on the
// previous/next day in the viewer's zone is flagged rather than silently moved.
function oneTime(
  utc: string | null | undefined,
  raw: string | null | undefined,
  tz: string | null,
  scheduledDate?: string | null,
): { text: string; dayOffset: number } {
  if (isValidZone(tz) && utc) {
    const instant = moment.utc(utc);
    if (instant.isValid()) {
      const local = instant.clone().tz(tz);
      let dayOffset = 0;
      if (scheduledDate) {
        const base = moment.tz(scheduledDate, 'YYYY-MM-DD', tz).startOf('day');
        if (base.isValid()) dayOffset = local.clone().startOf('day').diff(base, 'days');
      }
      return { text: local.format('HH:mm'), dayOffset };
    }
  }
  // Fallback: the raw stored value is already the employee-local time.
  return { text: hhmm(raw), dayOffset: 0 };
}

export interface ShiftDisplay {
  text: string;        // e.g. "09:00-18:00" (with "(+1d)" markers when a time crosses midnight)
  zone: string | null; // IANA zone the text is shown in, or null if unknown
  unknown: boolean;    // true when Agent Time was requested but the employee has no configured zone
}

function offsetMark(dayOffset: number): string {
  if (dayOffset > 0) return ` (+${dayOffset}d)`;
  if (dayOffset < 0) return ` (${dayOffset}d)`;
  return '';
}

export function describeShiftTimes(shift: ShiftTimeInput, mode: TimeDisplayMode, viewerTz: string): ShiftDisplay {
  if (!shift.startTime && !shift.endTime && !shift.startTimeUTC) {
    return { text: '', zone: null, unknown: false };
  }

  let tz: string | null;
  let unknown = false;
  if (mode === 'agent') {
    tz = resolveAgentTimezone(shift);
    unknown = !tz; // show raw employee-local time, but flag the zone as unknown
  } else {
    tz = isValidZone(viewerTz) ? viewerTz : null;
  }

  const start = oneTime(shift.startTimeUTC, shift.startTime, tz, shift.date);
  const end = oneTime(shift.endTimeUTC, shift.endTime, tz, shift.date);

  let text: string;
  if (start.text && end.text) {
    text = `${start.text}${offsetMark(start.dayOffset)}–${end.text}${offsetMark(end.dayOffset)}`;
  } else {
    text = start.text || end.text || '—';
  }
  return { text, zone: tz, unknown };
}

/** Human label for a zone, e.g. "America/Chicago (CDT)". refIso anchors the DST abbreviation to the shift's date. */
export function zoneLabel(tz: string | null, refIso?: string | null): string {
  if (!tz) return 'unknown';
  const m = refIso && moment.utc(refIso).isValid() ? moment.utc(refIso).tz(tz) : moment.tz(tz);
  const abbr = m.format('z');
  return abbr && !/^[+-]\d/.test(abbr) ? `${tz} (${abbr})` : tz;
}
